import React from 'react'
import type { QuickFilter, NumericFilterValue } from '../../../types/baseExplorerConfig'

const NUMERIC_OPERATORS: { value: NumericFilterValue['operator']; label: string }[] = [
  { value: 'eq', label: '= (equals)' },
  { value: 'neq', label: '!= (not equals)' },
  { value: 'gt', label: '> (greater than)' },
  { value: 'gte', label: '>= (greater or equal)' },
  { value: 'lt', label: '< (less than)' },
  { value: 'lte', label: '<= (less or equal)' },
  { value: 'between', label: 'Between' },
  { value: 'null', label: 'Is null' },
  { value: 'notNull', label: 'Is not null' },
]
import './FilterPanel.scss'

export interface FilterPanelProps {
  isOpen: boolean
  column: QuickFilter
  value: any
  onFilterChange: (field: string, value: any) => void
  onApply: () => void
  onClear: (field: string) => void
  onClose: () => void
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  isOpen,
  column,
  value,
  onFilterChange,
  onApply,
  onClear,
  onClose,
}) => {
  // Buffer all edits locally — only push to parent on Apply
  const [draft, setDraft] = React.useState<any>(value)
  const panelRef = React.useRef<HTMLDivElement>(null)

  // Sync draft when the panel opens for a different column or value resets externally
  React.useEffect(() => {
    setDraft(value)
  }, [column.field, value])

  // Auto-focus the first interactive control when the panel opens so the user
  // can immediately type / use arrow keys without an extra click.
  React.useEffect(() => {
    if (!isOpen) return
    const node = panelRef.current?.querySelector<HTMLElement>(
      '.filter-panel-body input, .filter-panel-body select, .filter-panel-body textarea',
    )
    node?.focus()
  }, [isOpen, column.field])

  if (!isOpen) return null

  const handleApply = () => {
    onFilterChange(column.field, draft)
    onApply()
  }

  const renderControl = () => {
    switch (column.type) {
      case 'dropdown':
        return (
          <select
            className="filter-control filter-select"
            value={draft ?? ''}
            onChange={(e) => setDraft(e.target.value || undefined)}
          >
            <option value="">All</option>
            {column.options?.map((opt: { value: string; label: string }) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )

      case 'textSearch':
        return (
          <input
            type="text"
            className="filter-control filter-text-input"
            placeholder={`Search ${column.label}...`}
            value={draft ?? ''}
            onChange={(e) => setDraft(e.target.value || undefined)}
          />
        )

      case 'dateRange': {
        const dateValue = draft ?? { from: '', to: '' }
        return (
          <div className="filter-date-range">
            <label htmlFor={`filter-${column.field}-from`}>From</label>
            <input
              id={`filter-${column.field}-from`}
              type="date"
              className="filter-control filter-date-input"
              value={dateValue.from ?? ''}
              onChange={(e) =>
                setDraft({ ...dateValue, from: e.target.value || undefined })
              }
            />
            <label htmlFor={`filter-${column.field}-to`}>To</label>
            <input
              id={`filter-${column.field}-to`}
              type="date"
              className="filter-control filter-date-input"
              value={dateValue.to ?? ''}
              onChange={(e) =>
                setDraft({ ...dateValue, to: e.target.value || undefined })
              }
            />
          </div>
        )
      }

      case 'numeric': {
        const numValue: NumericFilterValue = draft ?? { operator: 'eq' }
        const showValue = numValue.operator !== 'null' && numValue.operator !== 'notNull'
        const showValue2 = numValue.operator === 'between'
        return (
          <div className="filter-numeric">
            <select
              className="filter-control filter-select"
              value={numValue.operator}
              onChange={(e) => {
                const op = e.target.value as NumericFilterValue['operator']
                const next: NumericFilterValue = { operator: op }
                if (op !== 'null' && op !== 'notNull') {
                  next.value = numValue.value
                  if (op === 'between') next.value2 = numValue.value2
                }
                setDraft(next)
              }}
            >
              {NUMERIC_OPERATORS.map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
            {showValue && (
              <input
                type="number"
                className="filter-control filter-number-input"
                placeholder="Value"
                value={numValue.value ?? ''}
                onChange={(e) => {
                  const val = e.target.value === '' ? undefined : Number(e.target.value)
                  setDraft({ ...numValue, value: val })
                }}
              />
            )}
            {showValue2 && (
              <>
                <span className="filter-numeric-separator">and</span>
                <input
                  type="number"
                  className="filter-control filter-number-input"
                  placeholder="Value 2"
                  value={numValue.value2 ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? undefined : Number(e.target.value)
                    setDraft({ ...numValue, value2: val })
                  }}
                />
              </>
            )}
          </div>
        )
      }

      default:
        return null
    }
  }

  // Pressing Enter inside any input applies the filter (mirrors clicking Apply).
  // Skipped for textareas where Enter is a newline.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
      e.preventDefault();
      handleApply();
    }
  };

  return (
    <div
      ref={panelRef}
      className="filter-panel"
      data-testid="filter-panel"
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      onKeyDown={handleKeyDown}
    >
      <div className="filter-panel-header">
        <span className="filter-panel-title">
          <i className="fa fa-filter" /> {column.label}
        </span>
        <button
          className="filter-panel-close btn btn-sm"
          onClick={onClose}
          aria-label="Close filter panel"
        >
          <i className="fa fa-times" />
        </button>
      </div>
      <div className="filter-panel-body">
        {renderControl()}
      </div>
      <div className="filter-panel-footer">
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => onClear(column.field)}
          aria-label="Clear filter"
        >
          Clear
        </button>
        <button
          className="btn btn-sm btn-primary"
          onClick={handleApply}
          aria-label="Apply filter"
        >
          Apply
        </button>
      </div>
    </div>
  )
}
